import { useTranslation } from "react-i18next";
import { Check, Save, Trash2 } from "lucide-react";

import type { BlsState } from "../../domain/blsChannel";
import { MAX_SET_NOTE_LENGTH, SUD_VALUES, VOC_VALUES, type EmdrSetRecorder } from "../../domain/emdrSets";
import { useSetRatingDraft } from "../../application/useSetRatingDraft";

type Props = {
    state: BlsState | null;
    recorder: EmdrSetRecorder;
};

type ScaleProps = {
    label: string;
    prefix: "SUD" | "VOC";
    values: readonly number[];
    value: number | null;
    onChange: (value: number | null) => void;
};

/** A row of numbers, one pressable; pressing the chosen one again clears it. */
function RatingScale({ label, prefix, values, value, onChange }: ScaleProps) {
    return (
        <fieldset className="flex flex-col gap-1">
            <legend className="text-xs text-room-ink-muted">{label}</legend>
            <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
                {values.map((option) => (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={value === option}
                        aria-label={`${prefix} ${option}`}
                        onClick={() => onChange(value === option ? null : option)}
                        className={`min-h-8 min-w-8 rounded-md text-xs font-semibold tabular-nums ${
                            value === option ? "bg-room-accent text-white" : "bg-room-active text-room-ink hover:bg-room-hover"
                        }`}
                    >
                        {option}
                    </button>
                ))}
            </div>
        </fieldset>
    );
}

/**
 * The ratings after a set, entered while it is paused.
 *
 * The therapist asks, the patient answers aloud, the therapist presses: the
 * patient's screen shows none of this, and none of it travels over the call.
 *
 * Ratings chosen and not saved are saved anyway as soon as the set moves on:
 * see `useSetRatingDraft`.
 */
export default function EmdrSetForm({ state, recorder }: Props) {
    const { t } = useTranslation();
    const { paused, sud, setSud, voc, setVoc, note, setNote, saved, save, empty } = useSetRatingDraft(state, recorder);

    return (
        <div className="flex flex-col gap-2 border-t border-room-line-soft pt-3" data-testid="emdr-set-form">
            {paused && !saved && (
                <>
                    <RatingScale label={t("emdr.sets.sud")} prefix="SUD" values={SUD_VALUES} value={sud} onChange={setSud} />
                    <RatingScale label={t("emdr.sets.voc")} prefix="VOC" values={VOC_VALUES} value={voc} onChange={setVoc} />
                    <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                        {t("emdr.sets.note")}
                        <textarea
                            value={note}
                            maxLength={MAX_SET_NOTE_LENGTH}
                            rows={2}
                            onChange={(event) => setNote(event.target.value)}
                            className="rounded-lg border border-room-line bg-room-active px-2 py-1 text-sm text-room-ink"
                        />
                    </label>
                    <p className="text-xs text-room-ink-muted">{t("emdr.sets.noteHint")}</p>
                    <p className="text-xs text-room-ink-muted">{t("emdr.sets.autoSaveHint")}</p>
                    <button
                        type="button"
                        onClick={() => void save()}
                        disabled={empty || recorder.saving}
                        className="room-control room-control-primary self-start disabled:opacity-50"
                    >
                        <Save size={16} aria-hidden="true" />
                        <span>{t("emdr.sets.save")}</span>
                    </button>
                </>
            )}
            {paused && saved && (
                <p role="status" className="flex items-center gap-1 text-xs text-room-ok">
                    <Check size={14} aria-hidden="true" />
                    {t("emdr.sets.saved")}
                </p>
            )}

            {recorder.error && (
                <p role="alert" className="text-xs text-room-live">
                    {t(`emdr.sets.errors.${recorder.error}`)}
                </p>
            )}

            {recorder.sets.length > 0 && (
                <ol className="flex flex-col gap-1" aria-label={t("emdr.sets.listLabel")}>
                    {recorder.sets.map((set) => (
                        <li key={set.id} className="flex items-center justify-between gap-2 rounded-lg bg-room-active/60 px-2 py-1 text-xs text-room-ink">
                            <span className="tabular-nums">
                                {t("emdr.sets.row", { number: set.set_number, passes: set.passes })}
                                {set.sud !== null && ` · SUD ${set.sud}`}
                                {set.voc !== null && ` · VOC ${set.voc}`}
                            </span>
                            <button
                                type="button"
                                onClick={() => void recorder.remove(set.id)}
                                aria-label={t("emdr.sets.delete")}
                                className="flex size-7 items-center justify-center rounded-md text-room-ink-muted hover:bg-room-hover"
                            >
                                <Trash2 size={13} aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
